import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const AuthModal = () => {
  const { authModal, closeAuth, openAuth, login, signup, googleLogin } = useAuth();
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ name: "", family_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const active = authModal || mode;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (active === "login") {
        await login({ email: form.email, password: form.password });
      } else {
        await signup(form);
      }
      closeAuth();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {authModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/50 p-4 backdrop-blur-sm"
          onClick={closeAuth}
          data-testid="auth-modal-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl"
            data-testid="auth-modal"
          >
            <button
              onClick={closeAuth}
              data-testid="auth-modal-close"
              aria-label="Close"
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-[#F8F5EC] text-slate-500 transition-transform hover:scale-110"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-2">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[#FFC107]">
                <Star className="h-5 w-5 fill-white text-white" />
              </span>
              <div>
                <h2 className="font-display text-xl font-bold text-slate-900">
                  {active === "login" ? "Welcome back!" : "Create your parent account"}
                </h2>
                <p className="text-xs text-slate-500">Grown-ups hold the keys here.</p>
              </div>
            </div>

            <button
              onClick={googleLogin}
              data-testid="auth-google-btn"
              className="mt-6 flex w-full items-center justify-center gap-3 rounded-full border-2 border-[#E2DDD0] bg-white py-3 font-display text-base font-semibold text-slate-800 transition-all hover:border-slate-400"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.1 3.7-8.6z" />
                <path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-6-2.2-7-5.1l-3.9 3C3.2 21.3 7.3 24 12 24z" />
                <path fill="#FBBC05" d="M5 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3l-3.9-3C.4 8.3 0 10.1 0 12s.4 3.7 1.1 5.3l3.9-3z" />
                <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.2 2.7 1.1 6.7l3.9 3c1-2.9 3.8-5 7-5z" />
              </svg>
              Continue with Google
            </button>

            <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-slate-400">
              <span className="h-px flex-1 bg-[#E2DDD0]" /> or with email <span className="h-px flex-1 bg-[#E2DDD0]" />
            </div>

            <div className="mb-5 grid grid-cols-2 rounded-full bg-[#F8F5EC] p-1" data-testid="auth-mode-tabs">
              {["signup", "login"].map((m) => (
                <button
                  key={m}
                  onClick={() => openAuth(m)}
                  data-testid={`auth-tab-${m}`}
                  className={`rounded-full py-2 text-sm font-bold capitalize transition-all ${
                    active === m ? "bg-slate-900 text-white shadow" : "text-slate-500"
                  }`}
                >
                  {m === "signup" ? "Sign up" : "Log in"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="grid gap-3" data-testid="auth-form">
              {active === "signup" && (
                <>
                  <input
                    required
                    value={form.name}
                    onChange={set("name")}
                    data-testid="auth-name-input"
                    placeholder="Your name"
                    className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
                  />
                  <input
                    value={form.family_name}
                    onChange={set("family_name")}
                    data-testid="auth-family-input"
                    placeholder="Family name (e.g. Little Lights Family)"
                    className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
                  />
                </>
              )}
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                data-testid="auth-email-input"
                placeholder="Email"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
              />
              <input
                required
                type="password"
                minLength={8}
                value={form.password}
                onChange={set("password")}
                data-testid="auth-password-input"
                placeholder="Password (8+ characters)"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
              />
              {error && (
                <p className="rounded-2xl bg-[#FFE5E5] px-4 py-3 text-sm font-semibold text-[#DC2626]" data-testid="auth-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 py-3.5 font-display text-base font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {active === "login" ? "Log in" : "Create account"}
              </button>
              <p className="text-center text-xs text-slate-400">
                Accounts are for parents and guardians. No child accounts, ever.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
